/**
=========================================================
* Material Kit 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
 * The base typography styles for the Material Kit 2 PRO React.
 * You can add new typography style using this file.
 * You can customized the typography styles for the entire Material Kit 2 PRO React using thie file.
 */

// Material Kit 2 PRO React Base Styles
import colors from "assets/theme/base/colors";

// Material Kit 2 PRO React Helper Functions
import pxToRem from "assets/theme/functions/pxToRem";
const { text, primary } = colors;

const baseProperties = {
  // fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  // fontFamily2: '"Roboto Slab", sans-serif',
  fontFamily: '"Nunito Sans", "Helvetica", "Arial", sans-serif',
  fontFamily2: '"Nunito Sans", "Helvetica", "Arial", sans-serif',

  // fontFamily2: '"Poppins", sans-serif',
  fontWeightLighter: 100,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 800,
  fontSizeXXS: pxToRem(10.4),
  fontSizeXS: pxToRem(12),
  fontSizeSM: pxToRem(14),
  fontSizeMD: pxToRem(16),
  fontSizeLG: pxToRem(18),
  fontSizeXL: pxToRem(20),
  fontSize2XL: pxToRem(24),
  fontSize3XL: pxToRem(30),
};

const baseHeadingProperties = {
  color: `${primary.main} !important`,
  fontWeight: baseProperties.fontWeightBold,
  textTransform: "capitalize !important",
};

const baseDisplayProperties = {
  fontFamily: baseProperties.fontFamily,
  color: text.main,
  fontWeight: baseProperties.fontWeightLight,
  lineHeight: 1.2,
};

const mediaQueryForMobile = `@media (min-width : 576px)`;
const mediaQueryForTablet = `@media (min-width : 769px)`;
// const mediaQueryForLaptop = `@media (min-width : 1100px)`;
const mediaQueryForDeskTop = `@media (min-width : 1200px)`;

const typography = {
  fontFamily: baseProperties.fontFamily,
  fontWeightLighter: baseProperties.fontWeightLighter,
  fontWeightLight: baseProperties.fontWeightLight,
  fontWeightRegular: baseProperties.fontWeightRegular,
  fontWeightMedium: baseProperties.fontWeightMedium,
  fontWeightBold: baseProperties.fontWeightBold,

  h1: {
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(26),
    // lineHeight: 1.25,
    lineHeight: 1.1,

    [mediaQueryForMobile]: {
      fontSize: pxToRem(32),
    },
    [mediaQueryForTablet]: {
      fontSize: pxToRem(34),
    },
    // [mediaQueryForLaptop]: {
    //   fontSize: pxToRem(38),
    // },
    [mediaQueryForDeskTop]: {
      fontSize: pxToRem(40),
    },
    ...baseHeadingProperties,
  },

  h2: {
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(24),
    lineHeight: 1.1,
    [mediaQueryForMobile]: {
      fontSize: pxToRem(28),
    },
    [mediaQueryForTablet]: {
      fontSize: pxToRem(32),
    },
    // [mediaQueryForLaptop]: {
    //   fontSize: pxToRem(38),
    // },
    [mediaQueryForDeskTop]: {
      fontSize: pxToRem(34),
    },
    // [mediaQueryForMobile]: {
    // },
    // [mediaQueryForTablet]: {
    //   fontSize: pxToRem(32),
    // },
    ...baseHeadingProperties,
  },

  h3: {
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(20),
    [mediaQueryForMobile]: {
      fontSize: pxToRem(22),
    },
    [mediaQueryForTablet]: {
      fontSize: pxToRem(28),
    },
    // lineHeight: 1.375,
    lineHeight: 1.1,

    ...baseHeadingProperties,
  },

  h4: {
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(18),
    // lineHeight: 1.375,
    lineHeight: 1.2,

    ...baseHeadingProperties,
  },

  h5: {
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(16),
    // lineHeight: 1.375,
    lineHeight: 1.2,
    [mediaQueryForTablet]: {
      fontSize: pxToRem(24),
    },

    ...baseHeadingProperties,
  },

  h6: {
    fontFamily: baseProperties.fontFamily2,
    fontSize: pxToRem(16),
    lineHeight: 1.1,

    [mediaQueryForTablet]: {
      fontSize: pxToRem(18),
    },

    [mediaQueryForDeskTop]: {
      fontSize: pxToRem(20),
    },

    ...baseHeadingProperties,
  },

  subtitle1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXL,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.625,
  },

  subtitle2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
  },

  body1: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeLG,
    fontWeight: baseProperties.fontWeightRegular,
    lineHeight: 1.625,
    color: baseDisplayProperties.color,
  },

  body2: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeMD,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.6,
    color: baseDisplayProperties.color,
  },

  button: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeSM,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.5,
    textTransform: "uppercase",
  },

  caption: {
    fontFamily: baseProperties.fontFamily,
    fontSize: baseProperties.fontSizeXS,
    fontWeight: baseProperties.fontWeightLight,
    lineHeight: 1.25,
  },

  overline: {
    fontFamily: baseProperties.fontFamily,
  },

  d1: {
    fontSize: pxToRem(80),
    ...baseDisplayProperties,
  },

  d2: {
    fontSize: pxToRem(72),
    ...baseDisplayProperties,
  },

  d3: {
    fontSize: pxToRem(64),
    ...baseDisplayProperties,
  },

  d4: {
    fontSize: pxToRem(56),
    ...baseDisplayProperties,
  },

  d5: {
    fontSize: pxToRem(48),
    ...baseDisplayProperties,
  },

  d6: {
    fontSize: pxToRem(40),
    ...baseDisplayProperties,
  },

  size: {
    xxs: baseProperties.fontSizeXXS,
    xs: baseProperties.fontSizeXS,
    sm: baseProperties.fontSizeSM,
    md: baseProperties.fontSizeMD,
    lg: baseProperties.fontSizeLG,
    xl: baseProperties.fontSizeXL,
    "2xl": baseProperties.fontSize2XL,
    "3xl": baseProperties.fontSize3XL,
  },

  lineHeight: {
    sm: 1.25,
    md: 1.5,
    lg: 2,
  },
};

export default typography;
